import { Tooltip } from '@flik/shared';
import React, { useEffect, useRef, useState } from 'react';
import { quickActionsProviderRegistry } from '../actions/search/providers';
import { useScreenShareSelectionStore } from '../stores/screenShareSelectionStore';
import { useSessionState } from './SessionStateProvider';

// Quick action menu icon (3 dots)
const QuickActionIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="19" r="1" />
  </svg>
);

interface QuickActionMenuProps {
  onMenuToggle?: (isOpen: boolean) => void;
  onClearSearch?: () => void;
  requestWindowResize?: (width: number, height: number) => Promise<void>;
}

const QuickActionMenu: React.FC<QuickActionMenuProps> = ({
  onMenuToggle,
  onClearSearch,
  requestWindowResize,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const sessionHook = useSessionState();
  const { hasSession } = sessionHook;
  const { selectedSource, clearSelectedSource } =
    useScreenShareSelectionStore();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        onMenuToggle?.(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onMenuToggle]);

  // Close menu when pressing Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        onMenuToggle?.(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onMenuToggle]);

  // Get quick actions from providers
  const getQuickActions = () => {
    const context = {
      hasSession,
      isLoggedIn: true, // Assuming logged in if we're in the app
      isLoading: false, // Web app doesn't have loading states like desktop
      selectedSource,
      screenSources: [], // Web app doesn't have screen sources
      onClearSearch,
      requestWindowResize,
      createSession: async (_title: string, _participants: string[]) => {
        // Web app doesn't have session creation
        console.log('Session creation not implemented in web app');
      },
      endSession: async () => {
        // Web app doesn't have session ending
        console.log('Session ending not implemented in web app');
      },
      sessionHook,
      clearSelectedSource,
    };

    return quickActionsProviderRegistry.getActions(context);
  };

  const toggleMenu = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    onMenuToggle?.(newIsOpen);
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Quick action button */}
      <Tooltip
        content={!isOpen ? 'Quick actions' : ''}
        direction="left"
        className="z-50"
      >
        <button
          onClick={toggleMenu}
          className="p-2 rounded-lg transition-colors text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
        >
          <QuickActionIcon />
        </button>
      </Tooltip>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute left-0 top-0 w-44 bg-black border border-gray-700 rounded-md shadow-lg z-50 transform -translate-x-full -translate-y-1 -ml-1 mt-1">
          <div className="py-0">
            {getQuickActions().map((action) => (
              <button
                key={action.id}
                onClick={() => {
                  action.onClick();
                  setIsOpen(false);
                }}
                disabled={action.disabled}
                className="w-full px-2 py-1.5 text-left text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1.5 transition-colors text-xs rounded-sm"
                title={action.tooltip}
              >
                {action.icon}
                <span className="font-medium">{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickActionMenu;
